// utils/timelineUtils.js
import moment from 'moment';
import { 
  extractRouteTimeData, 
  calculateYardDepartureTime, 
  calculateStopTimes,
  calculateAverageDriveTime,
  secondsToMoment
} from './routeTimeUtils';
import { processRouteStops } from './routeDataUtils';

/**
 * Convert crews to timeline groups format
 * @param {Array} crews - Array of crew objects
 * @returns {Array} Timeline groups
 */
export const calculateTimelineGroups = (crews) => {
  if (!Array.isArray(crews)) return [];
  
  return crews.map(crew => ({
    id: crew._id,
    title: crew.name,
    color: crew.color || '#2196f3',
    textColor: crew.textColor || '#fff',
    height: 75,
  }));
};

/**
 * Create a drive time item for the timeline
 * @param {moment.Moment} fromTime - Start time
 * @param {moment.Moment} toTime - End time
 * @param {string} crewId - Crew ID
 * @param {number} index - Stop index
 * @param {boolean} isYardDrive - Whether this is a yard drive
 * @returns {Object} Timeline item
 */
export const createDriveTimeItem = (fromTime, toTime, crewId, index, isYardDrive = false) => {
  const baseId = `${crewId}-drive-${index}-${fromTime.valueOf()}`;
  
  return {
    id: baseId,
    group: crewId,
    title: isYardDrive ? '🏭➡️ Leave Yard' : '🚗 Travel',
    start_time: fromTime.valueOf(),
    end_time: toTime.valueOf(),
    itemType: 'drive',
    color: isYardDrive ? '#607d8b' : '#9e9e9e',
    className: isYardDrive ? 'drive-item yard-drive' : 'drive-item',
    isYardDrive,
    durationMinutes: Math.round(moment.duration(toTime.diff(fromTime)).asMinutes())
  };
};

/**
 * Create a stop item for the timeline
 * @param {Object} stop - Stop data
 * @param {moment.Moment} arrivalTime - Arrival time
 * @param {moment.Moment} departureTime - Departure time
 * @param {Object} crew - Crew object
 * @param {number} index - Stop index
 * @param {boolean} isActive - Whether this stop is currently selected
 * @returns {Object} Timeline item
 */
export const createStopItem = (stop, arrivalTime, departureTime, crew, index, isActive = false) => {
  const stopId = `${crew._id}-stop-${stop.siteId || index}-${arrivalTime.valueOf()}-${index}`;
  
  return {
    id: stopId,
    group: crew._id,
    title: `${stop.orderNumber}. ${stop.displayName}`,
    start_time: arrivalTime.valueOf(),
    end_time: departureTime.valueOf(),
    itemType: 'stop',
    color: crew.color || '#2196f3',
    stopData: stop,
    orderNumber: stop.orderNumber,
    totalStops: stop.totalStops,
    estimatedHours: stop.estimatedHours || 1,
    address: stop.address || '',
    isActive,
    className: isActive ? 'stop-item active' : 'stop-item',
    durationMinutes: Math.round(moment.duration(departureTime.diff(arrivalTime)).asMinutes())
  };
};

/**
 * Generate fallback timeline items when times are invalid
 * @param {Object} route - Route object
 * @param {Array} processedStops - Processed stops
 * @param {moment.Moment} startTime - Start time for the route
 * @param {Array} crews - Array of crews
 * @param {Object} crew - Crew object
 * @param {string} activeMarkerId - Active marker ID
 * @returns {Array} Timeline items
 */
const generateFallbackTimelineItems = (route, processedStops, startTime, crews, crew, activeMarkerId) => {
  const timelineItems = [];
  let currentTime = startTime.clone();
  
  const avgDriveTime = 15; // 15 minutes average drive time
  const defaultServiceTime = 60; // 1 hour default service time
  
  // Add initial yard departure
  const yardDepartureTime = currentTime.clone();
  currentTime.add(avgDriveTime, 'minutes');
  
  timelineItems.push(
    createDriveTimeItem(yardDepartureTime, currentTime, route.crewId, 'yard-start', true)
  );
  
  // Process each stop
  processedStops.forEach((stop, index) => {
    // Add drive time between stops
    if (index > 0) {
      const driveStart = currentTime.clone();
      currentTime.add(avgDriveTime, 'minutes');
      timelineItems.push(
        createDriveTimeItem(driveStart, currentTime, route.crewId, index, false)
      );
    }
    
    // Add the stop
    const arrivalTime = currentTime.clone();
    const serviceTime = (stop.estimatedHours || 1) * 60; // Convert hours to minutes
    currentTime.add(serviceTime, 'minutes');
    
    timelineItems.push(
      createStopItem(
        stop,
        arrivalTime,
        currentTime,
        crew,
        index,
        activeMarkerId === stop.siteId
      )
    );
  });
  
  // Add return to yard
  if (processedStops.length > 0) {
    const returnStart = currentTime.clone();
    currentTime.add(avgDriveTime, 'minutes');
    
    timelineItems.push({
      ...createDriveTimeItem(returnStart, currentTime, route.crewId, 'yard-return', false),
      title: '➡️🏭 Return to Yard',
      isYardReturn: true
    });
  }
  
  return timelineItems;
};

/**
 * Generate all timeline items for a route
 * @param {Object} route - Route object
 * @param {Array} processedStops - Processed stops from processRouteStops
 * @param {Date} selectedDate - Selected date
 * @param {Array} crews - Array of crews
 * @param {string} activeMarkerId - Currently active marker ID
 * @returns {Array} Timeline items
 */
export const generateTimelineItems = (
  route, 
  processedStops, 
  selectedDate, 
  crews = [], 
  activeMarkerId = null
) => {
  const timelineItems = [];
  
  if (!route || !processedStops || processedStops.length === 0) {
    return timelineItems;
  }
  
  const crew = crews.find(c => c._id === route.crewId);
  if (!crew) {
    console.warn(`Crew not found for route: ${route.crewId}`);
    return timelineItems;
  }
  
  const timeData = extractRouteTimeData(route);
  const hasOptimizedTimes = timeData.hasOptimizedTimes;
  
  let currentTime = moment(selectedDate).hour(6).minute(30); // Default start
  
  // Handle optimized routes with actual times
  if (hasOptimizedTimes) {
    const firstStop = processedStops[0];
    const firstArrival = firstStop.estimatedArrivalTime;
    
    // Check if arrival time is valid (not 0 or very small)
    if (!firstArrival || firstArrival < 3600) { // Less than 1 AM
      console.warn('Invalid arrival time detected:', firstArrival, 'for stop:', firstStop.displayName);
      // Use default morning start time
      const defaultStartTime = moment(selectedDate).hour(7).minute(0);
      return generateFallbackTimelineItems(route, processedStops, defaultStartTime, crews, crew, activeMarkerId);
    }
    
    // Use the actual drive duration from backend (already in minutes)
    const firstDriveTime = firstStop.estimatedDriveDuration || 15; // Default to 15 min if missing
    
    // Calculate yard departure
    const yardDepartureTime = calculateYardDepartureTime(selectedDate, firstArrival, firstDriveTime);
    
    // Add yard departure drive
    timelineItems.push(
      createDriveTimeItem(
        yardDepartureTime,
        secondsToMoment(selectedDate, firstArrival),
        route.crewId,
        'yard-start',
        true
      )
    );
    
    // Process each stop
    processedStops.forEach((stop, index) => {
      const { arrival, departure } = calculateStopTimes(stop, selectedDate);
      
      // Add drive time between stops
      if (index > 0 && arrival && departure) {
        const prevStop = processedStops[index - 1];
        const prevDeparture = secondsToMoment(selectedDate, prevStop.estimatedDepartureTime);
        
        if (prevDeparture && arrival) {
          timelineItems.push(
            createDriveTimeItem(prevDeparture, arrival, route.crewId, index, false)
          );
        }
      }
      
      // Add the stop
      if (arrival && departure) {
        timelineItems.push(
          createStopItem(
            stop, 
            arrival, 
            departure, 
            crew, 
            index,
            activeMarkerId === stop.siteId
          )
        );
      }
      
      // Update current time for last stop
      if (stop.isLastStop && departure) {
        currentTime = departure;
      }
    });
  } else {
    // Fallback for non-optimized routes - use 7 AM start time
    const defaultStartTime = moment(selectedDate).hour(7).minute(0);
    return generateFallbackTimelineItems(route, processedStops, defaultStartTime, crews, crew, activeMarkerId);
  }
  
  // Add return to yard drive for optimized routes
  if (currentTime && hasOptimizedTimes) {
    // Calculate return-to-yard time based on last stop
    const lastStop = processedStops[processedStops.length - 1];
    let returnDriveTime = 15; // Default fallback
    
    // If we have total drive time and can calculate used drive time
    if (route.totalDriveTime) {
      // Sum up all drive times to stops
      let totalDriveToStops = 0;
      processedStops.forEach((stop, index) => {
        if (stop.estimatedDriveDuration) {
          totalDriveToStops += stop.estimatedDriveDuration;
        }
      });
      
      // Return drive time = total drive time - drive time to all stops
      const calculatedReturnTime = route.totalDriveTime - totalDriveToStops;
      if (calculatedReturnTime > 0 && calculatedReturnTime < 120) { // Sanity check: between 0 and 2 hours
        returnDriveTime = Math.round(calculatedReturnTime);
      } else {
        // Fallback: use the drive time to first stop as estimate for return
        returnDriveTime = firstDriveTime || 15;
      }
    }
    
    const returnStart = currentTime.clone();
    const returnEnd = currentTime.clone().add(returnDriveTime, 'minutes');
    
    timelineItems.push({
      id: `${route.crewId}-drive-return-${returnStart.valueOf()}`,
      group: route.crewId,
      title: '➡️🏭 Return to Yard',
      start_time: returnStart.valueOf(),
      end_time: returnEnd.valueOf(),
      itemType: 'drive',
      color: '#607d8b',
      className: 'drive-item yard-drive',
      isYardReturn: true,
      durationMinutes: returnDriveTime
    });
  }
  
  return timelineItems;
};

/**
 * Generate timeline items for multiple routes
 * @param {Array} routes - Array of routes
 * @param {Date} selectedDate - Selected date
 * @param {Array} crews - Array of crews
 * @param {Array} sites - Array of sites
 * @param {string} activeMarkerId - Currently active marker ID
 * @returns {Array} All timeline items
 */
export const generateAllTimelineItems = (
  routes, 
  selectedDate, 
  crews, 
  sites, 
  activeMarkerId = null
) => {
  if (!Array.isArray(routes)) return [];
  
  const allItems = [];
  
  routes.forEach(route => {
    const processedStops = processRouteStops(route, sites);
    const routeItems = generateTimelineItems(
      route, 
      processedStops, 
      selectedDate, 
      crews, 
      activeMarkerId
    );
    allItems.push(...routeItems);
  });
  
  return allItems;
};

/**
 * Calculate timeline view bounds based on items
 * @param {Array} items - Timeline items
 * @param {Date} selectedDate - Selected date
 * @returns {Object} Timeline bounds
 */
export const calculateTimelineBounds = (items, selectedDate) => {
  if (!items || items.length === 0) {
    // Return default bounds
    const defaultStart = moment(selectedDate).hour(6);
    const defaultEnd = moment(selectedDate).hour(18);
    
    return {
      start: defaultStart.valueOf(),
      end: defaultEnd.valueOf(),
      duration: 12 * 60 // 12 hours in minutes
    };
  }
  
  // Find earliest and latest times
  let earliestTime = Infinity;
  let latestTime = -Infinity;
  
  items.forEach(item => {
    if (item.start_time < earliestTime) earliestTime = item.start_time;
    if (item.end_time > latestTime) latestTime = item.end_time;
  });
  
  // Add padding
  const paddingMinutes = 30;
  const start = moment(earliestTime).subtract(paddingMinutes, 'minutes');
  const end = moment(latestTime).add(paddingMinutes, 'minutes');
  
  return {
    start: start.valueOf(),
    end: end.valueOf(),
    duration: moment.duration(end.diff(start)).asMinutes()
  };
};

/**
 * Filter timeline items by criteria
 * @param {Array} items - Timeline items
 * @param {Object} filters - Filter criteria
 * @returns {Array} Filtered items
 */
export const filterTimelineItems = (items, filters = {}) => {
  if (!Array.isArray(items)) return [];
  
  return items.filter(item => {
    // Filter by crew
    if (filters.crewId && item.group !== filters.crewId) return false;
    
    // Filter by item type
    if (filters.itemType && item.itemType !== filters.itemType) return false;
    
    // Filter by time range
    if (filters.startTime && item.end_time < filters.startTime) return false;
    if (filters.endTime && item.start_time > filters.endTime) return false;
    
    return true;
  });
};

/**
 * Group timeline items by crew
 * @param {Array} items - Timeline items
 * @returns {Object} Items grouped by crew ID
 */
export const groupTimelineItemsByCrew = (items) => {
  if (!Array.isArray(items)) return {};
  
  return items.reduce((acc, item) => {
    const crewId = item.group;
    if (!acc[crewId]) {
      acc[crewId] = [];
    }
    acc[crewId].push(item);
    return acc;
  }, {});
};